import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
    LayoutDashboard,
    Users,
    ClipboardList,
    FolderOpen,
    MessageSquare,
    Bot,
    Settings,
    CheckSquare,
    Bell,
    FileText,
    ChevronLeft,
    ChevronRight,
    ListTodo,
    Clock,
    CheckCircle2,
    AlertTriangle,
} from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { hasPermission } from '../utils/permissions';
import { useMessages } from '../context/MessageContext';

// ─── Admin menu items ─────────────────────────────────
const adminMenuItems = [
    { icon: LayoutDashboard, label: 'Home', to: '/admin' },
    { icon: Users, label: 'People', to: '/admin/employees' },
    { icon: ClipboardList, label: 'Activity Centre', to: '/admin/activity-centre', matchPrefix: '/admin/activity-centre' },
    { icon: FolderOpen, label: 'Content', to: '/admin/files' },
    { icon: MessageSquare, label: 'Messages', to: '/admin/messages', hasBadge: true, badgeType: 'messages' },
    { icon: Bot, label: 'AI Assistant', to: '/admin/ai' },
    { icon: Settings, label: 'Settings', to: '/admin/settings' },
];

// ─── Employee menu items (dynamic basePath) ───────────
const getEmployeeMenuItems = (basePath) => [
    { icon: LayoutDashboard, label: 'Dashboard', to: basePath },
    { icon: CheckSquare, label: 'My Tasks', to: `${basePath}/tasks` },
    { icon: Bell, label: 'Notices', to: `${basePath}/notices` },
    { icon: MessageSquare, label: 'Messages', to: `${basePath}/messages`, hasBadge: true, badgeType: 'messages' },
    { icon: FileText, label: 'Documents', to: `${basePath}/files` },
    { icon: ClipboardList, label: 'Surveys', to: `${basePath}/surveys`, hasBadge: true, badgeType: 'surveys' },
    { icon: Bot, label: 'AI Assistant', to: `${basePath}/ai` },
    { icon: Settings, label: 'Settings', to: `${basePath}/settings` },
];

// ─── My Activities items (shared by all roles) ────────
const myActivitiesItems = [
    { icon: Clock, label: 'Pending', status: 'pending' },
    { icon: ListTodo, label: 'In Progress', status: 'in_progress' },
    { icon: CheckCircle2, label: 'Completed', status: 'completed' },
    { icon: AlertTriangle, label: 'Overdue', status: 'overdue' },
];

// ─── Permission filters (keys match permissions.config.js) ──────
const filterAdminItems = (items, perms) => {
    return items.filter(item => {
        if (item.label === 'Home') return true;
        switch (item.label) {
            case 'People':
                return hasPermission(perms, 'employee.view_details') ||
                    hasPermission(perms, 'employee.add');
            case 'Activity Centre':
                return hasPermission(perms, 'activity.view') ||
                    hasPermission(perms, 'activity.create') ||
                    hasPermission(perms, 'compliance.view_dashboard');
            case 'Content':
                return hasPermission(perms, 'file.view') ||
                    hasPermission(perms, 'file.upload');
            case 'Messages':
                return hasPermission(perms, 'messaging.send') ||
                    hasPermission(perms, 'messaging.view');
            case 'AI Assistant':
                return hasPermission(perms, 'ai.access');
            case 'Settings':
                return hasPermission(perms, 'system.settings') ||
                    hasPermission(perms, 'system.email_templates') ||
                    hasPermission(perms, 'role.create');
            default:
                return true;
        }
    });
};

const filterEmployeeItems = (items, perms) => {
    return items.filter(item => {
        if (item.label === 'Dashboard') return true;
        switch (item.label) {
            case 'My Tasks':
                return true; // Baseline: all employees can receive/complete tasks
            case 'Notices':
                return true; // Baseline: all employees receive notices
            case 'Documents':
                return hasPermission(perms, 'file.view');
            case 'Messages':
                return true; // Baseline: all employees can message
            case 'Surveys':
                return true; // Baseline: all employees can respond to surveys
            case 'AI Assistant':
                return hasPermission(perms, 'ai.access');
            case 'Settings':
                return true; // Everyone can access their own settings
            default:
                return true;
        }
    });
};

// ═══════════════════════════════════════════════════════
// Component
// ═══════════════════════════════════════════════════════
const UnifiedSidebar = ({ isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen, basePath = '/employee' }) => {
    const { user } = useAuth();
    const { unreadCount } = useMessages();
    const location = useLocation();
    const [surveyCount, setSurveyCount] = useState(0);
    const [activityCounts, setActivityCounts] = useState({ pending: 0, in_progress: 0, completed: 0, overdue: 0 });
    const [companyBranding, setCompanyBranding] = useState(null);

    const isAdmin = user?.is_admin;
    const perms = user?.permissions || {};

    // Determine menu items based on role
    const rawItems = isAdmin ? adminMenuItems : getEmployeeMenuItems(basePath);
    const filteredMenuItems = isAdmin ? filterAdminItems(rawItems, perms) : filterEmployeeItems(rawItems, perms);

    // Fetch company branding
    useEffect(() => {
        const fetchBranding = async () => {
            try {
                const response = await api.get('/company/branding');
                setCompanyBranding(response.data.company);
            } catch (error) {
                // Silently fail — show defaults
            }
        };
        fetchBranding();
    }, []);

    // Fetch survey count (employee only)
    useEffect(() => {
        if (isAdmin) return;
        const fetchSurveyCount = async () => {
            try {
                const response = await api.get('/surveys/employee/my-surveys');
                const surveys = response.data.surveys || [];
                const pendingCount = surveys.filter(s =>
                    s.status === 'pending' || s.status === 'in_progress'
                ).length;
                setSurveyCount(pendingCount);
            } catch (error) {
                console.error('Error fetching survey count:', error);
            }
        };

        fetchSurveyCount();
        const interval = setInterval(fetchSurveyCount, 60000);
        return () => clearInterval(interval);
    }, [isAdmin]);

    // Fetch My Activities counts
    useEffect(() => {
        const fetchActivityCounts = async () => {
            try {
                const response = await api.get('/my-activities/counts');
                setActivityCounts(response.data.counts || { pending: 0, in_progress: 0, completed: 0, overdue: 0 });
            } catch (error) {
                console.error('Error fetching activity counts:', error);
            }
        };

        fetchActivityCounts();
        const interval = setInterval(fetchActivityCounts, 60000);
        return () => clearInterval(interval);
    }, []);

    const handleLinkClick = () => {
        if (isMobileOpen) setIsMobileOpen(false);
    };

    // Calculate badge count for a given item
    const getBadgeCount = (item) => {
        if (item.badgeType === 'messages') return unreadCount;
        if (item.badgeType === 'surveys') return surveyCount;
        return 0;
    };

    // My activities base path
    const myActivitiesBasePath = isAdmin ? '/admin/my-activities' : `${basePath}/my-activities`;
    const totalActivityCount = activityCounts.pending + activityCounts.overdue;

    return (
        <>
            {/* Mobile Overlay */}
            {isMobileOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setIsMobileOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`
                    fixed top-0 left-0 h-full bg-card border-r border-border z-50
                    transition-all duration-300 ease-in-out flex flex-col
                    ${isCollapsed ? 'w-20' : 'w-64'}
                    ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'}
                    lg:translate-x-0
                `}
            >
                {/* Company Logo / Branding */}
                <div className="h-16 flex items-center px-4 border-b border-border">
                    <div className="flex items-center gap-2 min-w-0">
                        {companyBranding?.logo_url ? (
                            <img
                                src={companyBranding.logo_url}
                                alt={companyBranding.company_name || 'Company'}
                                className="w-8 h-8 rounded-lg object-cover shadow-md flex-shrink-0"
                            />
                        ) : (
                            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center text-primary-foreground font-bold text-sm shadow-md flex-shrink-0">
                                {(companyBranding?.company_name || 'E')[0].toUpperCase()}
                            </div>
                        )}
                        {!isCollapsed && (
                            <span className="font-semibold text-foreground text-lg truncate">
                                {companyBranding?.company_name || 'envion'}
                            </span>
                        )}
                    </div>
                </div>

                {/* Main Navigation */}
                <nav className="flex-1 py-4 px-3 overflow-y-auto">
                    <ul className="space-y-1">
                        {filteredMenuItems.map((item) => {
                            const isPrefixActive = item.matchPrefix && location.pathname.startsWith(item.matchPrefix);
                            const homeRoute = isAdmin ? '/admin' : basePath;

                            return (
                                <li key={item.to}>
                                    <NavLink
                                        to={item.to}
                                        onClick={handleLinkClick}
                                        end={item.to === homeRoute || !!item.matchPrefix}
                                        className={({ isActive: navIsActive }) => {
                                            const isActive = navIsActive || isPrefixActive;
                                            return `
                                                w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
                                                transition-all duration-200 group
                                                ${isActive
                                                    ? 'bg-accent text-accent-foreground shadow-lg shadow-accent/30'
                                                    : 'text-muted-foreground hover:bg-secondary'
                                                }
                                                ${isCollapsed ? 'justify-center' : ''}
                                            `;
                                        }}
                                    >
                                        {({ isActive: navIsActive }) => {
                                            const isActive = navIsActive || isPrefixActive;
                                            const badgeCount = item.hasBadge ? getBadgeCount(item) : 0;
                                            return (
                                                <>
                                                    <div className="relative">
                                                        <item.icon
                                                            size={20}
                                                            className={isActive ? 'text-accent-foreground' : 'text-muted-foreground group-hover:text-accent'}
                                                        />
                                                        {item.hasBadge && badgeCount > 0 && isCollapsed && (
                                                            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                                                                {badgeCount > 9 ? '9+' : badgeCount}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {!isCollapsed && (
                                                        <>
                                                            <span className={`font-medium flex-1 ${isActive ? '' : 'group-hover:text-foreground'}`}>
                                                                {item.label}
                                                            </span>
                                                            {item.hasBadge && badgeCount > 0 && (
                                                                <span className={`ml-auto px-2 py-0.5 text-xs font-bold rounded-full ${isActive ? 'bg-white text-accent' : 'bg-red-500 text-white'
                                                                    }`}>
                                                                    {badgeCount > 99 ? '99+' : badgeCount}
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                </>
                                            );
                                        }}
                                    </NavLink>
                                </li>
                            );
                        })}
                    </ul>

                    {/* ─── My Activities Section ─────────────── */}
                    <div className="mt-6 pt-4 border-t border-border">
                        {!isCollapsed && (
                            <div className="flex items-center justify-between px-3 mb-2">
                                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                    My Activities
                                </span>
                                {totalActivityCount > 0 && (
                                    <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-amber-100 text-amber-700">
                                        {totalActivityCount}
                                    </span>
                                )}
                            </div>
                        )}
                        {isCollapsed && (
                            <div className="flex justify-center mb-2">
                                <div className="w-6 h-[2px] bg-border rounded-full" />
                            </div>
                        )}
                        <ul className="space-y-1">
                            {myActivitiesItems.map((item) => {
                                const to = `${myActivitiesBasePath}?status=${item.status}`;
                                const count = activityCounts[item.status] || 0;

                                return (
                                    <li key={item.status}>
                                        <NavLink
                                            to={to}
                                            onClick={handleLinkClick}
                                            className={({ isActive }) =>
                                                `
                                                w-full flex items-center gap-3 px-3 py-2 rounded-xl
                                                transition-all duration-200 group text-sm
                                                ${isActive
                                                    ? 'bg-accent/10 text-accent-foreground'
                                                    : 'text-muted-foreground hover:bg-secondary'
                                                }
                                                ${isCollapsed ? 'justify-center' : ''}
                                                `
                                            }
                                        >
                                            {({ isActive }) => (
                                                <>
                                                    <div className="relative">
                                                        <item.icon
                                                            size={18}
                                                            className={isActive ? 'text-accent' : 'text-muted-foreground group-hover:text-accent'}
                                                        />
                                                        {count > 0 && isCollapsed && (
                                                            <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-amber-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                                                                {count > 9 ? '9+' : count}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {!isCollapsed && (
                                                        <>
                                                            <span className={`font-medium flex-1 ${isActive ? '' : 'group-hover:text-foreground'}`}>
                                                                {item.label}
                                                            </span>
                                                            {count > 0 && (
                                                                <span className={`ml-auto px-1.5 py-0.5 text-[10px] font-bold rounded-full ${item.status === 'overdue'
                                                                    ? 'bg-red-100 text-red-700'
                                                                    : item.status === 'completed'
                                                                        ? 'bg-green-100 text-green-700'
                                                                        : 'bg-gray-100 text-gray-600'
                                                                    }`}>
                                                                    {count > 99 ? '99+' : count}
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </NavLink>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </nav>

                {/* Collapse Toggle */}
                <div className="p-4 border-t border-border hidden lg:block">
                    <button
                        onClick={() => setIsCollapsed(!isCollapsed)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
                    >
                        {isCollapsed ? (
                            <ChevronRight size={20} />
                        ) : (
                            <>
                                <ChevronLeft size={20} />
                                <span className="text-sm font-medium">Collapse</span>
                            </>
                        )}
                    </button>
                </div>
            </aside>
        </>
    );
};

export default UnifiedSidebar;
